<script setup lang="ts">
import {ref, computed, watch, onMounted} from 'vue';
import {QForm, QField} from 'quasar';
import {useI18n} from 'vue-i18n';
import {useRequiredRule} from '@/ts/composables/pure/use-input-rules';
import {useDateFormat} from '@/ts/composables/pure/use-date-format';
import {DEPLOYMENT_COUNTRY} from '@/ts/utils/region';
import type {Language} from '@/ts/types/component/localization.type';

const {t, locale} = useI18n();

const name = defineModel<string>('name');
const birthDate = defineModel<Date | null>('birthDate');

const emit = defineEmits<{
  finish: []
}>();

const form = ref<QForm>(null!);
const dobFieldRef = ref<InstanceType<typeof QField> | null>(null);
const mmRef = ref<HTMLInputElement | null>(null);
const ddRef = ref<HTMLInputElement | null>(null);
const yyyyRef = ref<HTMLInputElement | null>(null);

const requiredRule = useRequiredRule();

const mm = ref('');
const dd = ref('');
const yyyy = ref('');

// Day-first (DD.MM.YYYY) or month-first (MM/DD/YYYY), the way every other date in the app is read
// once there is a profile: by country, not by language. There is no profile yet, so the country is
// the one this deployment gives every account it creates.
const datePatterns = computed(() => useDateFormat({
  countryCode: DEPLOYMENT_COUNTRY,
  language: locale.value as Language,
}));
const isDayFirst = computed((): boolean => !datePatterns.value.isMonthFirst);
const dateSeparator = computed((): string => datePatterns.value.numericDate.charAt(2));

const parsedBirthDate = computed((): Date | null => {
  if (mm.value.length !== 2 || dd.value.length !== 2 || yyyy.value.length !== 4) {
    return null;
  }
  const month = parseInt(mm.value, 10);
  const day = parseInt(dd.value, 10);
  const year = parseInt(yyyy.value, 10);
  if (isNaN(month) || isNaN(day) || isNaN(year) || year < 1900) {
    return null;
  }
  const date = new Date(year, month - 1, day);
  if (date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null;
  }
  return date;
});

function isAtLeast18(date: Date): boolean {
  const today = new Date();
  const cutoff = new Date(today.getFullYear() - 18, today.getMonth(), today.getDate());
  return date <= cutoff;
}

// Only surface a value upstream when the date is both valid and 18+, so the
// page can gate the Continue button on a genuinely complete, eligible entry.
watch(parsedBirthDate, (date) => {
  birthDate.value = date && isAtLeast18(date) ? date : null;
});

onMounted(() => {
  if (!birthDate.value) {
    return;
  }
  const d = birthDate.value;
  mm.value = String(d.getMonth() + 1).padStart(2, '0');
  dd.value = String(d.getDate()).padStart(2, '0');
  yyyy.value = String(d.getFullYear());
  if (mmRef.value) {
    mmRef.value.value = mm.value;
  }
  if (ddRef.value) {
    ddRef.value.value = dd.value;
  }
  if (yyyyRef.value) {
    yyyyRef.value.value = yyyy.value;
  }
});

function validateBirthDate(): boolean | string {
  if (!mm.value && !dd.value && !yyyy.value) {
    return t('required');
  }
  if (!parsedBirthDate.value) {
    return t('date_of_birth_invalid');
  }
  if (!isAtLeast18(parsedBirthDate.value)) {
    return t('date_of_birth_age_error');
  }
  return true;
}

function onMmInput(e: Event): void {
  const input = e.target as HTMLInputElement;
  mm.value = input.value.replace(/\D/gu, '').slice(0, 2);
  input.value = mm.value;
  if (mm.value.length === 2) {
    // Day-first: DD→MM→YYYY, after MM go to YYYY; month-first: MM→DD→YYYY, after MM go to DD
    (isDayFirst.value ? yyyyRef : ddRef).value?.focus();
  }
  if (yyyy.value.length === 4) {
    void dobFieldRef.value?.validate();
  }
}

function onDdInput(e: Event): void {
  const input = e.target as HTMLInputElement;
  dd.value = input.value.replace(/\D/gu, '').slice(0, 2);
  input.value = dd.value;
  if (dd.value.length === 2) {
    // Day-first: DD→MM→YYYY, after DD go to MM; month-first: MM→DD→YYYY, after DD go to YYYY
    (isDayFirst.value ? mmRef : yyyyRef).value?.focus();
  }
  if (yyyy.value.length === 4) {
    void dobFieldRef.value?.validate();
  }
}

function onYyyyInput(e: Event): void {
  const input = e.target as HTMLInputElement;
  yyyy.value = input.value.replace(/\D/gu, '').slice(0, 4);
  input.value = yyyy.value;
  if (yyyy.value.length === 4) {
    void dobFieldRef.value?.validate();
  }
}

function onMmBlur(): void {
  if (mm.value.length === 1) {
    mm.value = mm.value.padStart(2, '0');
    if (mmRef.value) {
      mmRef.value.value = mm.value;
    }
    if (yyyy.value.length === 4) {
      void dobFieldRef.value?.validate();
    }
  }
}

function onDdBlur(): void {
  if (dd.value.length === 1) {
    dd.value = dd.value.padStart(2, '0');
    if (ddRef.value) {
      ddRef.value.value = dd.value;
    }
    if (yyyy.value.length === 4) {
      void dobFieldRef.value?.validate();
    }
  }
}

function onMmKeydown(e: KeyboardEvent): void {
  if (e.key === 'Backspace' && mm.value === '') {
    e.preventDefault();
    // Day-first: backspace from MM goes to DD (DD is first)
    if (isDayFirst.value) {
      ddRef.value?.focus();
    }
  }
}

function onDdKeydown(e: KeyboardEvent): void {
  if (e.key === 'Backspace' && dd.value === '') {
    e.preventDefault();
    // Month-first: backspace from DD goes to MM (MM is first)
    if (!isDayFirst.value) {
      mmRef.value?.focus();
    }
  }
}

function onYyyyKeydown(e: KeyboardEvent): void {
  if (e.key === 'Backspace' && yyyy.value === '') {
    e.preventDefault();
    // Day-first: backspace from YYYY goes to MM; month-first: backspace from YYYY goes to DD
    (isDayFirst.value ? mmRef : ddRef).value?.focus();
  }
}

async function submitForm(): Promise<void> {
  const isValid = await form.value.validate();
  if (!isValid) {
    return;
  }
  emit('finish');
}

defineExpose({
  submitForm,
});
</script>
<template>
  <div class="sign-up-personalize">
    <h1>{{ $t('personalize_your_experience') }}</h1>
    <q-form ref="form" @submit.prevent="submitForm">
      <q-input
        v-model="name"
        :rules="[requiredRule]"
        :label="$t('whats_your_name')"
        autocomplete="name"
      />
      <q-field
        ref="dobFieldRef"
        model-value=""
        :label="$t('date_of_birth')"
        stack-label
        lazy-rules="ondemand"
        :rules="[validateBirthDate]"
        class="dob-field"
      >
        <template #control>
          <div class="dob-inputs">
            <template v-if="isDayFirst">
              <input
                ref="ddRef"
                :value="dd"
                type="text"
                inputmode="numeric"
                maxlength="2"
                :aria-label="$t('date_label_day')"
                :placeholder="$t('date_placeholder_day')"
                class="dob-segment"
                @input="onDdInput"
                @keydown="onDdKeydown"
                @blur="onDdBlur"
              />
              <span class="dob-sep">{{ dateSeparator }}</span>
              <input
                ref="mmRef"
                :value="mm"
                type="text"
                inputmode="numeric"
                maxlength="2"
                :aria-label="$t('date_label_month')"
                :placeholder="$t('date_placeholder_month')"
                class="dob-segment"
                @input="onMmInput"
                @keydown="onMmKeydown"
                @blur="onMmBlur"
              />
            </template>
            <template v-else>
              <input
                ref="mmRef"
                :value="mm"
                type="text"
                inputmode="numeric"
                maxlength="2"
                :aria-label="$t('date_label_month')"
                :placeholder="$t('date_placeholder_month')"
                class="dob-segment"
                @input="onMmInput"
                @keydown="onMmKeydown"
                @blur="onMmBlur"
              />
              <span class="dob-sep">{{ dateSeparator }}</span>
              <input
                ref="ddRef"
                :value="dd"
                type="text"
                inputmode="numeric"
                maxlength="2"
                :aria-label="$t('date_label_day')"
                :placeholder="$t('date_placeholder_day')"
                class="dob-segment"
                @input="onDdInput"
                @keydown="onDdKeydown"
                @blur="onDdBlur"
              />
            </template>
            <span class="dob-sep">{{ dateSeparator }}</span>
            <input
              ref="yyyyRef"
              :value="yyyy"
              type="text"
              inputmode="numeric"
              maxlength="4"
              :aria-label="$t('date_label_year')"
              :placeholder="$t('date_placeholder_year')"
              class="dob-segment dob-segment--year"
              @input="onYyyyInput"
              @keydown="onYyyyKeydown"
              @blur="() => dobFieldRef?.validate()"
            />
          </div>
        </template>
        <template #append>
          <q-icon name="info" size="xs" class="info-icon">
            <q-tooltip>{{ $t('birth_year_info') }}</q-tooltip>
          </q-icon>
        </template>
      </q-field>
    </q-form>
  </div>
</template>
<style scoped lang="scss">
h1 {
  @include font-display-4;
  margin-bottom: var(--Spacing-spacing-6);
}

.sign-up-personalize {
  display: flex;
  flex-direction: column;
}

:deep(form) {
  display: flex;
  flex-direction: column;
  gap: var(--Spacing-spacing-4);
}

.dob-field {
  :deep(.q-field__control) {
    height: auto;
    min-height: 56px;
    align-items: flex-end;
    padding-bottom: 4px;
  }

  :deep(.q-field__label) {
    top: 8px;
  }
}

.dob-inputs {
  display: flex;
  align-items: center;
  gap: var(--Spacing-spacing-3);
  width: 100%;
  padding-bottom: 2px;
}

.dob-segment {
  background: transparent;
  border: none;
  outline: none;
  // No font-size of its own: `inherit` used to win over the global field floor and left these
  // three segments at 14px while the field around them was 16, so iOS zoomed on focus (IPD-3064).
  font-family: inherit;
  color: inherit;
  width: 32px;
  padding: 0;
  min-width: 0;

  &::placeholder {
    color: var(--Neutral-UI-neutral-ui-50);
  }
}

.dob-segment--year {
  width: 52px;
}

.dob-sep {
  // Matches the segments, which the global input floor holds at 16px.
  font-size: max(16px, var(--Typography-Body));
  color: var(--Neutral-UI-neutral-ui-50);
  user-select: none;
}

.info-icon {
  cursor: help;
}
</style>
